import React from 'react';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import useScrollReveal from '../useScrollReveal';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';


export default function DashboardPreviewSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'simple';
    const metrics = content.metrics || [];
    const categories = content.categories || [];
    const insights = content.insights || [];
    const totalLabel = content.total_label || 'Total este mes';
    const totalValue = content.total_value || '';
    const totalChange = content.total_change || '';

    const Card = ({ children, className = '' }) => (
        <div className={`rounded-2xl p-4 sm:p-5 transition-all ${s.dark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-100 shadow-lg'} ${className}`}>
            {children}
        </div>
    );

    const MetricCard = ({ item }) => (
        <Card className="text-center">
            {item.icon && <span className="text-2xl mb-2 block">{item.icon}</span>}
            <p className={`text-2xl sm:text-3xl font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{item.value}</p>
            <p className={`text-sm mt-1 ${s.dark ? 'text-white/60' : 'text-gray-500'}`}>{item.label}</p>
            {item.change && (
                <span className={`inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded-full ${item.change.startsWith('-') || item.change.startsWith('↓')
                    ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {item.change}
                </span>
            )}
        </Card>
    );

    const CategoryBar = ({ item, maxAmount }) => {
        const pct = maxAmount > 0 ? (parseFloat(item.amount) / maxAmount) * 100 : 50;
        return (
            <div className="flex items-center gap-3">
                {item.icon && <span className="text-lg w-6 text-center shrink-0">{item.icon}</span>}
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between text-sm mb-1">
                        <span className={`font-medium ${s.dark ? 'text-white' : 'text-gray-800'}`}>{item.name}</span>
                        <span className={`font-semibold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{item.amount_label || item.amount}</span>
                    </div>
                    <div className={`h-2 rounded-full overflow-hidden ${s.dark ? 'bg-white/10' : 'bg-gray-100'}`}>
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: item.color || primaryColor }} />
                    </div>
                </div>
                {item.percent && <span className={`text-xs font-medium shrink-0 ${s.dark ? 'text-white/50' : 'text-gray-400'}`}>{item.percent}</span>}
            </div>
        );
    };

    const InsightCard = ({ item }) => (
        <div className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${s.dark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'}`}>
            {item.icon && <span className="text-xl shrink-0 mt-0.5">{item.icon}</span>}
            <div className="min-w-0">
                <p className={`text-sm font-semibold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{item.title}</p>
                <p className={`text-xs mt-0.5 ${s.dark ? 'text-white/50' : 'text-gray-500'}`}>{item.description}</p>
            </div>
        </div>
    );

    const maxAmt = Math.max(...categories.map(c => parseFloat(c.amount) || 0), 1);

    // Split variant — text left, dashboard right
    if (variant === 'split') {
        return (
            <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} position="top" color={s.accent} />

                <div ref={ref} className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
                        <div>
                            <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} align="left" />
                            {insights.length > 0 && <div className="mt-6 space-y-3">{insights.map((it, i) => <InsightCard key={i} item={it} />)}</div>}
                        </div>
                        <div className="space-y-4">
                            {totalValue && (
                                <Card>
                                    <p className={`text-sm ${s.dark ? 'text-white/60' : 'text-gray-500'}`}>{totalLabel}</p>
                                    <div className="flex items-end gap-2 mt-1">
                                        <span className={`text-3xl font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{totalValue}</span>
                                        {totalChange && <span className={`text-sm font-semibold mb-1 ${totalChange.includes('-') || totalChange.includes('↓') ? 'text-green-500' : 'text-red-500'}`}>{totalChange}</span>}
                                    </div>
                                </Card>
                            )}
                            {categories.length > 0 && <Card><div className="space-y-3">{categories.map((c, i) => <CategoryBar key={i} item={c} maxAmount={maxAmt} />)}</div></Card>}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
            </section>
        );
    }

    // Default: centered dashboard mockup
    return (
        <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} position="top" color={s.accent} />
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="mt-8 space-y-4">
                    {/* Top metrics row */}
                    {metrics.length > 0 && (
                        <div className={`grid ${metrics.length <= 2 ? 'grid-cols-2' : metrics.length === 3 ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-4'} gap-3 sm:gap-4`}>
                            {metrics.map((m, i) => <MetricCard key={i} item={m} />)}
                        </div>
                    )}
                    {/* Summary + categories */}
                    <div className={`grid ${insights.length > 0 ? 'md:grid-cols-2' : ''} gap-4`}>
                        <div className="space-y-4">
                            {totalValue && (
                                <Card>
                                    <p className={`text-sm ${s.dark ? 'text-white/60' : 'text-gray-500'}`}>{totalLabel}</p>
                                    <div className="flex items-end gap-2 mt-1">
                                        <span className={`text-3xl font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{totalValue}</span>
                                        {totalChange && <span className={`text-sm font-semibold mb-1 ${totalChange.includes('-') || totalChange.includes('↓') ? 'text-green-500' : 'text-red-500'}`}>{totalChange}</span>}
                                    </div>
                                </Card>
                            )}
                            {categories.length > 0 && <Card><div className="space-y-3">{categories.map((c, i) => <CategoryBar key={i} item={c} maxAmount={maxAmt} />)}</div></Card>}
                        </div>
                        {insights.length > 0 && <Card><div className="space-y-2">{insights.map((it, i) => <InsightCard key={i} item={it} />)}</div></Card>}
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
        </section>
    );
}
